import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaKey, FaArrowLeft, FaCheck } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import logo from '../assets/logo.png';
import { showToast } from '../components/ui/Toast';
import './ForgotPassword.css'; // Reusing the same CSS
import api from '../services/api';

const ResetPasswordOTP = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { email } = location.state || {};
  const otpInputs = Array(6).fill(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleOtpChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits are entered
    if (newOtp.every(digit => digit !== '') && index === 5) {
      handleVerifyOtp({ preventDefault: () => {} });
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input on backspace
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      // Move left with arrow key
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      // Move right with arrow key
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const response = await api.post('/users/verify-otp/', { email, otp: otpCode });
      const data = response.data;
      
      if (data.success) {
        setSuccess('Verification successful!');
        showToast.success('OTP verified successfully');
        navigate('/new-password', { 
          state: { 
            email,
            token: data.token || otpCode
          } 
        });
      } else {
        throw new Error(data.message || 'Invalid verification code');
      }
    } catch (error) {
      const errorMsg = error.message || 'Failed to verify code. Please try again.';
      setError(errorMsg);
      showToast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendCode = async (e) => {
    e.preventDefault();
    if (countdown > 0) return;
    
    try {
      setError('');
      const response = await api.post('/users/send-otp/', { email });
      const data = response.data;
      
      if (data.success) {
        setCountdown(60);
        showToast.success('Verification code resent!');
      } else {
        throw new Error(data.message || 'Failed to resend code');
      }
    } catch (error) {
      const errorMsg = error.message || 'Failed to resend code. Please try again.';
      setError(errorMsg);
      showToast.error(errorMsg);
    }
  };

  return (
    <Layout>
      <div className="forgot-password-page-container">
        <div className="forgot-password-split-screen">
          <div className="forgot-password-left-panel">
            <div className="forgot-password-content">
              <img src={logo} alt="UniTrade Logo" className="forgot-password-logo" />
              <h2>Verify Your Identity</h2>
              <p>For security, we've sent a verification code to your email address.</p>
            </div>
          </div>
          
          <div className="forgot-password-right-panel">
            <div className="forgot-password-form-container">
              <button 
                type="button" 
                className="back-button" 
                onClick={() => navigate('/forgot-password')}
              >
                <FaArrowLeft /> Back
              </button>
              
              <h3>Enter Verification Code</h3>
              <p className="forgot-subtitle">We've sent a 6-digit code to {email || 'your email'}</p>
              
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              
              <form onSubmit={handleVerifyOtp} className="forgot-password-form">
                <div className="otp-container">
                  {otpInputs.map((_, index) => (
                    <div key={index} className="otp-input-wrapper">
                      <input
                        ref={el => inputRefs.current[index] = el}
                        type="text"
                        className="otp-input"
                        value={otp[index]}
                        onChange={(e) => handleOtpChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        maxLength={1}
                        disabled={isLoading}
                        autoComplete="one-time-code"
                      />
                      {index !== 5 && <span className="otp-divider">-</span>}
                    </div>
                  ))}
                </div>
                
                <button 
                  type="submit" 
                  className="forgot-btn"
                  disabled={isLoading || otp.some(digit => digit === '')}
                >
                  {isLoading ? (
                    'Verifying...'
                  ) : (
                    <>
                      Verify Code <FaCheck className="ms-2" />
                    </>
                  )}
                </button>
                
                <div className="text-center mt-3">
                  <p className="forgot-subtitle mb-1">
                    {countdown > 0 
                      ? `Resend code in ${countdown}s` 
                      : "Didn't receive a code?"}
                  </p>
                  <button
                    type="button"
                    className="resend-link"
                    onClick={handleResendCode}
                    disabled={countdown > 0}
                  >
                    Resend Code
                  </button>
                </div>
                
                <div className="text-center mt-4">
                  <button 
                    type="button" 
                    className="resend-link"
                    onClick={() => navigate('/forgot-password')}
                  >
                    <FaArrowLeft className="me-1" /> Back to Email
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ResetPasswordOTP;
